import { useState, useCallback } from 'react';
import { Route, X, Baby, User, Music } from 'lucide-react';
import { ALL_ZONES, ZONE_TO_COLOR_REGION, ENTRANCE_CATEGORY_COLORS, DEAD_END_DESTINATIONS, DEAD_END_BY_ID } from '../../data/entrance-types';
import { usePathing } from '../hooks/use-pathing';
import type { PathResult, PathStep } from '../types';

const NAVIGABLE_ZONES = ALL_ZONES.filter(
  z => z !== 'Warps' && z !== 'Spawns' && z !== 'Owls',
);

const DEAD_END_DUNGEONS = DEAD_END_DESTINATIONS.filter(d => d.group === 'dungeon');
const DEAD_END_BOSSES = DEAD_END_DESTINATIONS.filter(d => d.group === 'boss');
const DEAD_END_SPECIAL = DEAD_END_DESTINATIONS.filter(d => d.group === 'special');

/** Resolve a node ID to its display label (zone name or dead-end label) */
function getNodeDisplayLabel(nodeId: string): string {
  const de = DEAD_END_BY_ID.get(nodeId);
  return de ? de.label : nodeId;
}

const REGION_COLORS: Record<string, string> = {
  Forest: '#22c55e',
  Hyrule: '#eab308',
  Kakariko: '#a855f7',
  'Death Mountain': '#ef4444',
  Zora: '#3b82f6',
  Desert: '#f97316',
  Special: '#6b7280',
};

function getZoneColor(zone: string): string {
  const region = ZONE_TO_COLOR_REGION[zone];
  return region ? REGION_COLORS[region] ?? '#6b7280' : '#6b7280';
}

interface PathFinderPanelProps {
  onClose: () => void;
}

export function PathFinderPanel({ onClose }: PathFinderPanelProps) {
  const { findPath, songs, toggleSong, songMap } = usePathing();

  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [includeWarps, setIncludeWarps] = useState(false);
  const [includeSpawns, setIncludeSpawns] = useState(false);
  const [results, setResults] = useState<PathResult[] | null>(null);
  const [showSongs, setShowSongs] = useState(false);

  const handleSearch = useCallback(() => {
    if (!to) return;
    if (!from && !includeWarps && !includeSpawns) return;

    const allPaths: PathResult[] = [];
    const seenKeys = new Set<string>();

    const addPaths = (paths: PathResult[]) => {
      for (const p of paths) {
        const key = p.steps.map(s => s.zone).join('->');
        if (!seenKeys.has(key)) {
          seenKeys.add(key);
          allPaths.push(p);
        }
      }
    };

    // Main from->to search
    if (from && from !== to) {
      addPaths(findPath(from, to));
    }

    // Include warps as additional starting points
    if (includeWarps) {
      addPaths(findPath('Warps', to));
    }

    // Include spawns as additional starting points
    if (includeSpawns) {
      addPaths(findPath('Spawns', to));
    }

    // Sort by path length
    allPaths.sort((a, b) => a.steps.length - b.steps.length);
    setResults(allPaths.slice(0, 10));
  }, [from, to, includeWarps, includeSpawns, findPath]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  }, [handleSearch]);

  return (
    <div className="flex flex-col h-full bg-gray-900/95 border-l border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <Route size={16} className="text-zelda-gold" />
          <span className="text-sm font-bold text-zelda-gold">Pathfinder</span>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-white">
          <X size={16} />
        </button>
      </div>

      {/* Controls */}
      <div className="p-3 space-y-2 border-b border-gray-700">
        {/* From / To selects */}
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="text-[10px] text-gray-500 uppercase tracking-wider">De</label>
            <select
              value={from}
              onChange={e => { setFrom(e.target.value); setResults(null); }}
              onKeyDown={handleKeyDown}
              className="w-full mt-0.5 px-2 py-1 text-xs bg-gray-800 border border-gray-600 rounded text-white"
            >
              <option value="">-- Zone --</option>
              {NAVIGABLE_ZONES.map(z => (
                <option key={z} value={z}>{z}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end pb-1 text-gray-500 text-xs">&rarr;</div>
          <div className="flex-1">
            <label className="text-[10px] text-gray-500 uppercase tracking-wider">Vers</label>
            <select
              value={to}
              onChange={e => { setTo(e.target.value); setResults(null); }}
              onKeyDown={handleKeyDown}
              className="w-full mt-0.5 px-2 py-1 text-xs bg-gray-800 border border-gray-600 rounded text-white"
            >
              <option value="">-- Destination --</option>
              <optgroup label="Zones">
                {NAVIGABLE_ZONES.map(z => (
                  <option key={z} value={z}>{z}</option>
                ))}
              </optgroup>
              {DEAD_END_SPECIAL.length > 0 && (
                <optgroup label="Lieux">
                  {DEAD_END_SPECIAL.map(d => (
                    <option key={d.id} value={d.id}>{d.label}</option>
                  ))}
                </optgroup>
              )}
              <optgroup label="Donjons">
                {DEAD_END_DUNGEONS.map(d => (
                  <option key={d.id} value={d.id}>{d.label}</option>
                ))}
              </optgroup>
              <optgroup label="Boss">
                {DEAD_END_BOSSES.map(d => (
                  <option key={d.id} value={d.id}>{d.label}</option>
                ))}
              </optgroup>
            </select>
          </div>
        </div>

        {/* Songs toggle */}
        <div>
          <button
            onClick={() => setShowSongs(!showSongs)}
            className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-zelda-gold"
          >
            <Music size={10} />
            Songs {showSongs ? '▲' : '▼'}
          </button>
          {showSongs && (
            <div className="mt-1 grid grid-cols-2 gap-x-2 gap-y-0.5">
              {Object.entries(songMap).map(([displayName, ootrName]) => (
                <label
                  key={ootrName}
                  className="flex items-center gap-1 text-[10px] text-gray-300 cursor-pointer hover:text-white"
                >
                  <input
                    type="checkbox"
                    checked={!!songs[ootrName]}
                    onChange={() => toggleSong(ootrName)}
                    className="w-3 h-3 accent-zelda-gold"
                  />
                  {displayName}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Additional sources */}
        <div className="flex gap-3">
          <label className="flex items-center gap-1 text-[10px] text-gray-300 cursor-pointer hover:text-white">
            <input
              type="checkbox"
              checked={includeSpawns}
              onChange={() => { setIncludeSpawns(!includeSpawns); setResults(null); }}
              className="w-3 h-3 accent-orange-500"
            />
            + Spawns
          </label>
          <label className="flex items-center gap-1 text-[10px] text-gray-300 cursor-pointer hover:text-white">
            <input
              type="checkbox"
              checked={includeWarps}
              onChange={() => { setIncludeWarps(!includeWarps); setResults(null); }}
              className="w-3 h-3 accent-cyan-500"
            />
            + Warp Songs
          </label>
        </div>

        {/* Search button */}
        <button
          onClick={handleSearch}
          disabled={!to || (!from && !includeWarps && !includeSpawns)}
          className="w-full px-3 py-1.5 text-xs font-medium bg-zelda-gold/20 text-zelda-gold border border-zelda-gold/30 rounded hover:bg-zelda-gold/30 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Chercher un chemin
        </button>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {results === null && (
          <p className="text-xs text-gray-500 text-center mt-4">
            Selectionnez une zone de depart et d'arrivee
          </p>
        )}

        {results !== null && results.length === 0 && (
          <div className="text-xs text-red-400 text-center mt-4 space-y-1">
            <p>Aucun chemin trouve</p>
            <p className="text-gray-500">
              Verifiez que les entrances sont trackees et que vous avez les items necessaires
            </p>
          </div>
        )}

        {results !== null && results.length > 0 && (
          <>
            <p className="text-[10px] text-gray-500">
              {results.length} chemin{results.length > 1 ? 's' : ''} trouve{results.length > 1 ? 's' : ''}
            </p>
            {results.map((result, idx) => (
              <PathResultCard key={idx} result={result} index={idx} />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function PathResultCard({ result, index }: { result: PathResult; index: number }) {
  return (
    <div className="bg-gray-800/50 border border-gray-700 rounded p-2 space-y-1.5">
      {/* Header with accessibility badges */}
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-gray-500">Chemin {index + 1} ({result.steps.length - 1} etape{result.steps.length - 1 > 1 ? 's' : ''})</span>
        <div className="flex gap-1">
          <span
            className={`flex items-center gap-0.5 text-[9px] px-1 rounded ${
              result.childAccessible
                ? 'bg-green-900/40 text-green-400 border border-green-800'
                : 'bg-red-900/30 text-red-400/60 border border-red-900'
            }`}
            title={result.childAccessible ? 'Accessible en enfant' : 'Inaccessible en enfant'}
          >
            <Baby size={9} />
            Enfant
          </span>
          <span
            className={`flex items-center gap-0.5 text-[9px] px-1 rounded ${
              result.adultAccessible
                ? 'bg-green-900/40 text-green-400 border border-green-800'
                : 'bg-red-900/30 text-red-400/60 border border-red-900'
            }`}
            title={result.adultAccessible ? 'Accessible en adulte' : 'Inaccessible en adulte'}
          >
            <User size={9} />
            Adulte
          </span>
        </div>
      </div>

      {/* Path visualization */}
      <div className="flex flex-col gap-1">
        {result.steps.map((step, i) => (
          <PathStepRow key={i} step={step} isFirst={i === 0} />
        ))}
      </div>
    </div>
  );
}

/**
 * Build a display label for the entrance used to reach this step.
 * For overworld: use the label as-is (e.g., "HF => ZR")
 * For non-overworld: prefix with fromZone if not already present (e.g., "KAK Windmill")
 */
function getEdgeDisplayLabel(edge: PathStep['edge']): string {
  if (!edge) return '';
  const label = edge.label;

  // For overworld, the label is usually "ZoneA => ZoneB" which is clear
  if (edge.category === 'overworld') return label;

  // For warps, show the song name
  if (edge.category === 'warp') return label;

  // For non-overworld (interior, grotto, dungeon, boss, owl, spawn):
  // prefix with fromZone if the label doesn't already start with it
  const fromPrefix = edge.fromZone + ' ';
  if (label.startsWith(fromPrefix) || label.startsWith(edge.fromZone + '::')) {
    return label;
  }
  return `${edge.fromZone} ${label}`;
}

function PathStepRow({ step, isFirst }: { step: PathStep; isFirst: boolean }) {
  const zoneColor = getZoneColor(step.zone);
  const displayLabel = getNodeDisplayLabel(step.zone);
  const isDeadEnd = step.zone.startsWith('de:');

  if (isFirst) {
    // Starting zone - just show the zone badge
    return (
      <div className="flex items-center gap-1.5">
        <span
          className="px-1.5 py-0.5 text-[10px] font-bold rounded border min-w-[3rem] text-center"
          style={{
            backgroundColor: zoneColor + '20',
            borderColor: zoneColor + '50',
            color: zoneColor,
          }}
        >
          {displayLabel}
        </span>
      </div>
    );
  }

  // Subsequent steps: show arrow + entrance name + destination zone
  const edgeLabel = getEdgeDisplayLabel(step.edge);
  const fromColor = step.edge ? getZoneColor(step.edge.fromZone) : zoneColor;
  const categoryColor = step.edge ? ENTRANCE_CATEGORY_COLORS[step.edge.category] : undefined;
  const isOneWay = step.edge?.oneWay || step.edge?.category === 'warp' || step.edge?.category === 'owl';

  return (
    <div className="flex items-center gap-1.5">
      {/* Arrow with category color — dashed for one-way */}
      <span
        className="text-[10px] pl-2"
        style={{ color: categoryColor ?? '#6b7280' }}
      >
        {isOneWay ? '⇢' : '↳'}
      </span>
      {/* Entrance label colored by source zone — italic for one-way */}
      <span
        className={`text-[10px] truncate ${isOneWay ? 'italic opacity-80' : ''}`}
        style={{ color: fromColor }}
        title={step.edge?.label}
      >
        {edgeLabel}
        {isOneWay && <span className="text-[8px] text-gray-500 ml-0.5">(one-way)</span>}
      </span>
      {/* Arrow — dashed for one-way */}
      <span className="text-[9px] text-gray-500">{isOneWay ? '⇥' : '→'}</span>
      {/* Destination badge + arrival entrance */}
      <span className="flex items-center gap-1 flex-shrink-0">
        <span
          className={`px-1.5 py-0.5 text-[10px] font-bold rounded min-w-[3rem] text-center ${
            isDeadEnd ? 'border-2 border-dotted' : isOneWay ? 'border border-dashed' : 'border'
          }`}
          style={{
            backgroundColor: zoneColor + '20',
            borderColor: zoneColor + '50',
            color: zoneColor,
          }}
        >
          {displayLabel}
        </span>
        {step.edge?.arrivalLabel && (
          <span
            className="text-[9px] opacity-60"
            style={{ color: zoneColor }}
          >
            ({step.edge.arrivalLabel})
          </span>
        )}
      </span>
    </div>
  );
}
